import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navigation, Clock, AlertCircle, Loader2, MapPin, X } from "lucide-react";

/**
 * Banner shown above the dining session when the reservation is still in
 * "booked" state. Responsibilities:
 *   1. Display a live countdown to the reservation time.
 *   2. Compute walking/driving ETA using browser geolocation + backend haversine.
 *   3. Send a one-shot browser notification when it's time to leave.
 *   4. Provide actions: recompute, enable notifications, "I'm here", snooze.
 *
 * Split into small subcomponents for clarity and testability.
 */
export default function LeaveOnTimeBanner({ reservation, restaurant, onCheckIn, checkingIn }) {
    const [snoozed, setSnoozed] = useState(false);
    const reservationTime = reservation?.reservation_time;
    const countdown = useReservationCountdown(reservationTime);
    const { eta, loading: etaLoading, error: etaError, refresh: refreshEta } = useEta(restaurant?.id);
    useLeaveNowNotification({ eta, reservationTime, restaurantName: restaurant?.name, countdown });

    if (snoozed) return null;

    const leaveNow = isTimeToLeave(eta, reservationTime);

    return (
        <Card
            className="mb-4 border-l-4 border-l-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/0.08)]"
            data-testid="leave-on-time-banner"
        >
            <CardContent className="p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <BannerIcon />
                    <BannerMessage leaveNow={leaveNow} countdown={countdown} eta={eta} etaError={etaError} />
                    <BannerActions
                        loadingEta={etaLoading}
                        checkingIn={checkingIn}
                        onRecalculate={refreshEta}
                        onEnableNotifications={enableNotifications}
                        onIAmHere={() => onCheckIn(false)}
                        onSnooze={() => setSnoozed(true)}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function isTimeToLeave(eta, reservationTime) {
    if (!eta || !reservationTime) return false;
    const diffMin = (new Date(reservationTime).getTime() - Date.now()) / 60000;
    return diffMin <= eta.eta_minutes + 8;
}

async function enableNotifications() {
    if (!("Notification" in window)) return;
    try {
        const result = await Notification.requestPermission();
        if (result === "granted") {
            new Notification("ReserveLite notifications enabled", {
                body: "We'll remind you when to leave.",
            });
        }
    } catch (error) {
        console.warn("[leave-banner] enableNotifications failed", error?.message);
    }
}

/* ------------------------------------------------------------------ */
/* Hooks                                                              */
/* ------------------------------------------------------------------ */

function useReservationCountdown(reservationTime) {
    const compute = useCallback(() => {
        if (!reservationTime) return "";
        const diff = new Date(reservationTime).getTime() - Date.now();
        if (diff <= 0) return "now";
        const totalMin = Math.floor(diff / 60000);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    }, [reservationTime]);

    const [countdown, setCountdown] = useState(compute);

    useEffect(() => {
        setCountdown(compute());
        const id = setInterval(() => setCountdown(compute()), 30 * 1000);
        return () => clearInterval(id);
    }, [compute]);

    return countdown;
}

function useEta(restaurantId) {
    const [eta, setEta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!restaurantId) return;
        setError(null);
        setLoading(true);
        try {
            const pos = await new Promise((resolve, reject) => {
                if (!("geolocation" in navigator)) {
                    reject(new Error("Geolocation not supported"));
                    return;
                }
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 });
            });
            const { data } = await api.post("/utility/eta", {
                restaurant_id: restaurantId,
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
            });
            setEta(data);
        } catch (e) {
            console.warn("[leave-banner] ETA fetch failed", e?.message);
            setError(e?.message || "Location unavailable");
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { eta, loading, error, refresh };
}

function useLeaveNowNotification({ eta, reservationTime, restaurantName, countdown }) {
    const [notifSent, setNotifSent] = useState(false);
    useEffect(() => {
        if (!eta || !reservationTime || notifSent) return;
        if (!("Notification" in window) || Notification.permission !== "granted") return;
        const diffMin = (new Date(reservationTime).getTime() - Date.now()) / 60000;
        const buffer = 5;
        if (diffMin <= eta.eta_minutes + buffer && diffMin >= -10) {
            try {
                new Notification("Time to head out!", {
                    body: `${restaurantName} • ETA ${Math.round(eta.eta_minutes)} min • reservation in ${countdown}`,
                });
                setNotifSent(true);
            } catch (error) {
                console.warn("[leave-banner] notification failed", error?.message);
            }
        }
    }, [eta, reservationTime, restaurantName, countdown, notifSent]);
}

/* ------------------------------------------------------------------ */
/* Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function BannerIcon() {
    return (
        <div className="h-10 w-10 rounded-full bg-[hsl(var(--brand-gold)/0.25)] grid place-items-center">
            <Clock className="h-5 w-5 text-[hsl(var(--brand-ink))]" />
        </div>
    );
}

function BannerMessage({ leaveNow, countdown, eta, etaError }) {
    return (
        <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Heads up</div>
            <div className="font-display text-xl leading-tight">
                {leaveNow ? "Time to leave for your reservation" : "Reservation upcoming"}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
                Starts in{" "}
                <strong className="text-foreground" data-testid="leave-on-time-countdown">
                    {countdown}
                </strong>
                {eta && (
                    <>
                        {" "}· ETA <strong className="text-foreground">{Math.round(eta.eta_minutes)} min</strong> ({eta.distance_km} km)
                    </>
                )}
            </div>
            {etaError && (
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" /> {etaError}
                </div>
            )}
        </div>
    );
}

function BannerActions({ loadingEta, checkingIn, onRecalculate, onEnableNotifications, onIAmHere, onSnooze }) {
    return (
        <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onRecalculate} disabled={loadingEta} data-testid="leave-on-time-refresh">
                {loadingEta ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1" />}
                Recalculate
            </Button>
            <Button variant="outline" size="sm" onClick={onEnableNotifications} data-testid="enable-notifications-button">
                Notify me
            </Button>
            <Button size="sm" onClick={onIAmHere} disabled={checkingIn} data-testid="banner-check-in-button">
                {checkingIn ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Navigation className="h-3.5 w-3.5 mr-1" />}
                I&apos;m here
            </Button>
            <Button variant="ghost" size="icon" onClick={onSnooze} aria-label="Snooze" data-testid="leave-on-time-snooze-button">
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}
